import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

import { Modal } from '@/components/ui/modal'
import { Textarea } from '@/components/ui/controls'
import { DataTable } from '@/components/ui/data-table'
import { toast as message } from 'sonner'
import { StatusBadge, Notice } from '../products/PageHelpers'
import { ArrowLeft, CircleStop } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  cancelSubscription,
  getSubscription,
  getSubscriptionInvoices,
} from '../../services/subscriptions'
import { formatTimestamp } from '../../utils/format'
import { formatAmount } from '../../utils/orderDisplay'
import type {
  MerchantSubscriptionDetail,
  MerchantSubscriptionInvoiceSummary,
  Pagination,
  SubscriptionCancelType,
} from '../../types'
import {
  billingReasonText,
  canCancelSubscription,
  cancellationStatusMeta,
  cancelTypeText,
  formatCard,
  formatSubscriptionTerm,
  invoiceStatusMeta,
  orderStatusMeta,
  subscriptionStatusMeta,
} from './subscriptionLabels'

const initialPagination: Pagination = {
  page: 1,
  size: 20,
  total: 0,
  totalPages: 0,
}

export default function SubscriptionDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [subscription, setSubscription] =
    useState<MerchantSubscriptionDetail | null>(null)
  const [invoices, setInvoices] = useState<
    MerchantSubscriptionInvoiceSummary[]
  >([])
  const [invoicePagination, setInvoicePagination] =
    useState<Pagination>(initialPagination)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [invoiceLoadError, setInvoiceLoadError] = useState<string | null>(null)
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelType, setCancelType] =
    useState<SubscriptionCancelType>('PERIOD_END')
  const [cancelReason, setCancelReason] = useState('')
  const [submittingCancel, setSubmittingCancel] = useState(false)

  const loadDetail = async () => {
    if (!id) return
    setLoading(true)
    try {
      const res = await getSubscription(id)
      setSubscription(res.data)
      setLoadError(null)
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : '載入訂閱詳情失敗'
      message.error(errorMessage)
      setLoadError(errorMessage)
      setSubscription(null)
    } finally {
      setLoading(false)
    }
  }

  const loadInvoices = async (
    page: number = 1,
    size: number = initialPagination.size,
  ) => {
    if (!id) return
    setInvoiceLoading(true)
    try {
      const res = await getSubscriptionInvoices(id, { page, size })
      setInvoices(res.data.items)
      setInvoicePagination(res.data.pagination)
      setInvoiceLoadError(null)
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : '載入賬單記錄失敗'
      message.error(errorMessage)
      setInvoiceLoadError(errorMessage)
      setInvoices([])
      setInvoicePagination(initialPagination)
    } finally {
      setInvoiceLoading(false)
    }
  }

  useEffect(() => {
    loadDetail()
    loadInvoices(1)
  }, [id])

  const openCancelModal = (nextCancelType: SubscriptionCancelType) => {
    setCancelType(nextCancelType)
    setCancelReason('')
    setCancelModalOpen(true)
  }

  const submitCancel = async () => {
    if (!id) return
    setSubmittingCancel(true)
    try {
      await cancelSubscription(id, {
        cancelAtPeriodEnd: cancelType === 'PERIOD_END',
        reason: cancelReason.trim() || undefined,
        requestKey: `${id}:${cancelType}:${Date.now()}`,
      })
      message.success(
        cancelType === 'PERIOD_END' ? '已提交週期末取消' : '已提交立即取消',
      )
      setCancelModalOpen(false)
      await Promise.all([
        loadDetail(),
        loadInvoices(invoicePagination.page, invoicePagination.size),
      ])
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '取消訂閱失敗')
    } finally {
      setSubmittingCancel(false)
    }
  }

  if (loading) {
    return (
      <div
        role="status"
        className="flex min-h-40 items-center justify-center text-muted-foreground"
      >
        載入中…
      </div>
    )
  }

  if (!subscription) {
    return (
      <Notice
        tone={'error'}
        title={loadError ? '載入訂閱詳情失敗' : '訂閱不存在或無權訪問'}
        description={loadError || undefined}
        action={
          loadError ? (
            <Button
              onClick={loadDetail}
              variant={'outline'}
              type="button"
              size={'sm'}
            >
              重新載入
            </Button>
          ) : undefined
        }
      />
    )
  }

  const statusMeta = subscriptionStatusMeta[subscription.status]
  const latestCancellation = subscription.latestCancellationRequest
  const canCancel = canCancelSubscription(subscription.status)

  const invoiceColumns = [
    {
      key: 'id',
      header: '賬單 ID',
      render: (row: MerchantSubscriptionInvoiceSummary) => row['id'] ?? '-',
    },
    {
      key: 'cycleNo',
      header: '期數',
      className: 'text-right tabular-nums',
      render: (row: MerchantSubscriptionInvoiceSummary) =>
        row['cycleNo'] ?? '-',
    },
    {
      key: 'billingReason',
      header: '類型',
      render: (row: MerchantSubscriptionInvoiceSummary) => {
        const value = row['billingReason']
        return billingReasonText[value]
      },
    },
    {
      key: 'amount',
      header: '金額',
      className: 'text-right tabular-nums',
      render: (row: MerchantSubscriptionInvoiceSummary) => {
        const record = row
        return `${formatAmount(record.amount)} ${record.currency}`
      },
    },
    {
      key: 'status',
      header: '狀態',
      render: (row: MerchantSubscriptionInvoiceSummary) => {
        const value = row['status']
        const meta = invoiceStatusMeta[value]
        return <StatusBadge color={meta.color}>{meta.label}</StatusBadge>
      },
    },
    {
      key: 'period',
      header: '週期',
      className: 'text-xs text-muted-foreground',
      render: (row: MerchantSubscriptionInvoiceSummary) => {
        const record = row
        return (
          <span>
            {formatTimestamp(record.periodStartAt)} ～{' '}
            {formatTimestamp(record.periodEndAt)}
          </span>
        )
      },
    },
    {
      key: 'paidAt',
      header: '支付時間',
      className: 'text-xs text-muted-foreground',
      render: (row: MerchantSubscriptionInvoiceSummary) => {
        const value = row['paidAt']
        return formatTimestamp(value)
      },
    },
    {
      key: 'order',
      header: '關聯訂單',
      render: (row: MerchantSubscriptionInvoiceSummary) => {
        const record = row
        if (!record.order) return '-'
        const meta = orderStatusMeta[record.order.status]
        return (
          <div className={'flex flex-col gap-3'}>
            <Button
              onClick={() => navigate(`/dashboard/orders/${record.order?.id}`)}
              style={{ padding: 0 }}
              variant={'link'}
              type="button"
              size={'sm'}
            >
              {record.order.id}
            </Button>
            <span>
              {formatAmount(record.order.amount)} {record.order.currency}{' '}
              <StatusBadge color={meta.color}>{meta.label}</StatusBadge>
            </span>
          </div>
        )
      },
    },
  ]

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 24,
        }}
      >
        <div className={'flex flex-wrap items-center gap-2'}>
          <Button
            onClick={() => navigate('/dashboard/subscriptions')}
            variant={'outline'}
            type="button"
          >
            {<ArrowLeft />}返回訂閱列表
          </Button>
          <h2
            style={{ margin: 0 }}
            className="mb-4 text-xl font-semibold tracking-tight"
          >
            訂閱詳情
          </h2>
        </div>
        <div className={'flex flex-wrap items-center gap-2'}>
          <StatusBadge color={statusMeta.color}>{statusMeta.label}</StatusBadge>
          {canCancel && (
            <>
              <Button
                onClick={() => openCancelModal('PERIOD_END')}
                variant={'outline'}
                type="button"
              >
                {<CircleStop />}週期末取消
              </Button>
              <Button
                onClick={() => openCancelModal('IMMEDIATE')}
                variant={'destructive'}
                type="button"
              >
                {<CircleStop />}立即取消
              </Button>
            </>
          )}
        </div>
      </div>

      <div style={{ width: '100%' }} className={'flex flex-col gap-3'}>
        <Card>
          <div className="space-y-4 px-5">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-4">
              <h3 className="font-semibold">{'基本信息'}</h3>
            </div>
            <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border md:grid-cols-2">
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'訂閱 ID'}
                </dt>
                <dd className="break-words text-sm">{subscription.id}</dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">{'狀態'}</dt>
                <dd className="break-words text-sm">
                  <StatusBadge color={statusMeta.color}>
                    {statusMeta.label}
                  </StatusBadge>
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">{'產品'}</dt>
                <dd className="break-words text-sm">
                  {subscription.productName || subscription.productId}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">{'計劃'}</dt>
                <dd className="break-words text-sm">
                  {subscription.plan.name || subscription.plan.id}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'客戶郵箱'}
                </dt>
                <dd className="break-words text-sm">
                  {subscription.customerEmail || '-'}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'客戶名稱'}
                </dt>
                <dd className="break-words text-sm">
                  {subscription.customerName || '-'}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'創建時間'}
                </dt>
                <dd className="break-words text-sm">
                  {formatTimestamp(subscription.createdAt)}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'更新時間'}
                </dt>
                <dd className="break-words text-sm">
                  {formatTimestamp(subscription.updatedAt)}
                </dd>
              </div>
            </dl>
          </div>
        </Card>
        <Card>
          <div className="space-y-4 px-5">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-4">
              <h3 className="font-semibold">{'計費與支付方式'}</h3>
            </div>
            <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border md:grid-cols-2">
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'計劃金額'}
                </dt>
                <dd className="break-words text-sm">
                  {formatAmount(subscription.plan.amount)}
                  {subscription.plan.currency}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'計費週期'}
                </dt>
                <dd className="break-words text-sm">
                  {formatSubscriptionTerm(
                    subscription.plan.billingInterval,
                    subscription.plan.billingIntervalCount,
                    subscription.plan.totalBillingCycles,
                  )}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'當前週期開始'}
                </dt>
                <dd className="break-words text-sm">
                  {formatTimestamp(subscription.currentPeriodStartAt)}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'當前週期結束'}
                </dt>
                <dd className="break-words text-sm">
                  {formatTimestamp(subscription.currentPeriodEndAt)}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'下次扣款'}
                </dt>
                <dd className="break-words text-sm">
                  {formatTimestamp(subscription.nextBillingAt)}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'支付方式'}
                </dt>
                <dd className="break-words text-sm">
                  信用卡{' '}
                  {formatCard(
                    subscription.card?.brand,
                    subscription.card?.last4,
                  )}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'卡有效期'}
                </dt>
                <dd className="break-words text-sm">
                  {subscription.card?.expMonth && subscription.card?.expYear
                    ? `${subscription.card.expMonth}/${subscription.card.expYear}`
                    : '-'}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'最近扣款'}
                </dt>
                <dd className="break-words text-sm">
                  {subscription.latestInvoice
                    ? `${formatAmount(subscription.latestInvoice.amount)} ${subscription.latestInvoice.currency} · ${invoiceStatusMeta[subscription.latestInvoice.status].label}`
                    : '-'}
                </dd>
              </div>
            </dl>
          </div>
        </Card>
        <Card>
          <div className="space-y-4 px-5">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-4">
              <h3 className="font-semibold">{'取消狀態'}</h3>
            </div>
            <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border md:grid-cols-2">
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'週期末取消'}
                </dt>
                <dd className="break-words text-sm">
                  {subscription.cancelAtPeriodEnd ? '是' : '否'}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'計劃取消時間'}
                </dt>
                <dd className="break-words text-sm">
                  {formatTimestamp(subscription.cancelAt)}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'實際取消時間'}
                </dt>
                <dd className="break-words text-sm">
                  {formatTimestamp(subscription.cancelledAt)}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'最近取消請求'}
                </dt>
                <dd className="break-words text-sm">
                  {latestCancellation ? (
                    <div className={'flex flex-col gap-3'}>
                      <span>
                        {cancelTypeText[latestCancellation.cancelType]} ·{' '}
                        <StatusBadge
                          color={
                            cancellationStatusMeta[latestCancellation.status]
                              .color
                          }
                        >
                          {
                            cancellationStatusMeta[latestCancellation.status]
                              .label
                          }
                        </StatusBadge>
                      </span>
                      <span className="text-muted-foreground">
                        {formatTimestamp(latestCancellation.requestedAt)}
                      </span>
                    </div>
                  ) : (
                    '-'
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </Card>
        <Card>
          <div className="space-y-4 px-5">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-4">
              <h3 className="font-semibold">{'歷史賬單 / 訂單記錄'}</h3>
            </div>
            {invoiceLoadError && (
              <Notice
                tone={'error'}
                title={'載入賬單記錄失敗'}
                description={invoiceLoadError}
                action={
                  <Button
                    onClick={() =>
                      loadInvoices(
                        invoicePagination.page || 1,
                        invoicePagination.size || initialPagination.size,
                      )
                    }
                    variant={'outline'}
                    type="button"
                    size={'sm'}
                  >
                    重新載入
                  </Button>
                }
                style={{ marginBottom: 16 }}
              />
            )}
            <DataTable
              rowKey={'id'}
              loading={invoiceLoading}
              columns={invoiceColumns}
              data={invoices}
              pagination={{
                page: invoicePagination.page,
                size: invoicePagination.size,
                total: invoicePagination.total,
                onChange: (page, size) => loadInvoices(page, size),
              }}
            />
          </div>
        </Card>
      </div>

      <Modal
        title={cancelType === 'PERIOD_END' ? '週期末取消訂閱' : '立即取消訂閱'}
        open={cancelModalOpen}
        onOpenChange={(open) => {
          if (!open) (() => setCancelModalOpen(false))()
        }}
        footer={
          <>
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
              {'取消'}
            </Button>
            <Button disabled={Boolean(submittingCancel)} onClick={submitCancel}>
              {cancelType === 'PERIOD_END' ? '確認週期末取消' : '確認立即取消'}
            </Button>
          </>
        }
      >
        <div style={{ width: '100%' }} className={'flex flex-col gap-3'}>
          {cancelType === 'PERIOD_END' ? (
            <Notice
              tone={'info'}
              title={'當前週期結束前訂閱仍有效，到期後不再續費。'}
            />
          ) : (
            <Notice
              tone={'warning'}
              title={'立即取消只阻止後續續費，不會自動退款。'}
              description={'如需退還已支付週期費用，請到對應訂單發起退款流程。'}
            />
          )}
          <fieldset className="flex gap-4">
            <legend className="sr-only">取消方式</legend>
            {(['PERIOD_END', 'IMMEDIATE'] as const).map((value) => (
              <label key={value} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="cancel-type"
                  checked={cancelType === value}
                  onChange={() => setCancelType(value)}
                />
                {value === 'PERIOD_END' ? '週期末取消' : '立即取消'}
              </label>
            ))}
          </fieldset>
          <p style={{ marginBottom: 0 }} className="text-muted-foreground">
            本操作只會使用 Kyren 訂閱 ID，不會向商戶展示或要求輸入上游通道信息。
          </p>
          <Textarea
            rows={4}
            maxLength={500}
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            placeholder={'取消原因（可選，僅供商戶與運營排查）'}
          />
        </div>
      </Modal>
    </div>
  )
}
